import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SettingsIcon, GitMergeIcon, WorkflowIcon, DatabaseIcon, MessageSquareIcon, FileTextIcon, CheckCircleIcon, ArrowRightIcon, SearchIcon, MapIcon, WrenchIcon, ActivityIcon, RocketIcon, LayersIcon, CpuIcon, AlertCircleIcon, ChevronDownIcon, ZapIcon } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { ScrollReveal } from '../components/ScrollReveal';
import { AnimatePresence } from 'framer-motion';
const faqs = [{
  question: 'What kind of automations can you build?',
  answer: 'We can automate almost any repetitive digital task. Common examples include syncing leads from Facebook Ads to your CRM, automatically sending estimates after a site visit, triggering review requests when a job is marked complete, and routing missed calls to an AI text-back system.'
}, {
  question: 'How long does a custom automation project take?',
  answer: 'Simple automations (like connecting a web form to a Google Sheet) can be done in a few days. Complex, multi-step workflows involving custom API integrations and conditional logic typically take 2-4 weeks to build, test, and deploy.'
}, {
  question: 'What tools and platforms can you integrate?',
  answer: 'We can integrate with any software that has an open API. We frequently work with ServiceTitan, Jobber, Housecall Pro, GoHighLevel, Salesforce, HubSpot, QuickBooks, Stripe, Google Workspace, and Meta/Google Ads.'
}, {
  question: 'Do I need to know how to code?',
  answer: 'No. We handle all the technical architecture, coding, and API connections. We deliver a finished, working system and provide you with a simple dashboard to monitor it, along with training for your team.'
}, {
  question: 'How much does a custom automation cost?',
  answer: 'Pricing depends entirely on the complexity of the workflow and the systems involved. We start with a free scoping call to understand your needs, then provide a flat-rate project fee or include it in a broader monthly growth package.'
}, {
  question: 'Can you automate workflows I already have in place?',
  answer: 'Yes. We often audit existing, manual workflows (like copy-pasting data between spreadsheets) and build automated systems to replace them, saving hours of administrative work each week and eliminating human error.'
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-800 last:border-0" data-id="element-1386">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-1387">
        <span className="text-lg font-bold text-white group-hover:text-viridia transition-colors" data-id="element-1388">
          {question}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-viridia' : ''}`} data-id="element-1389" />
      </button>
      <AnimatePresence data-id="element-1390">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-1391">
            <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-1392">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function CustomAutomationsPage() {
  return <div className="pt-24 pb-16" data-id="element-1393">
      <SEOHead title="Custom Automations & API Integrations | Viridia Analytics" description="Bespoke automation workflows for local businesses. Connect your tools, eliminate manual data entry, and build systems tailored to your unique operations." canonical="/custom-automations" keywords="custom automations, API integrations, workflow automation, business process automation, Zapier alternative, custom webhooks, Phoenix automation agency" faq={faqs} data-id="element-1394" />

      {/* Hero Section */}
      <section className="relative pt-12 pb-20 overflow-hidden" data-id="element-1395">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-viridia/10 via-viridia-black to-viridia-black -z-10" data-id="element-1396"></div>

        {/* Background Grid Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTTAgNDBoNDBWMEgwem0zOS0zOXYzOEgxVjFoMzh6IiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMDIpIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] -z-10 opacity-50" data-id="element-1397"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1398">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1399">
            <ScrollReveal data-id="element-1400">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-viridia/10 border border-viridia/20 text-viridia text-sm font-medium mb-6" data-id="element-1401">
                <SettingsIcon className="w-4 h-4" data-id="element-1402" />
                <span data-id="element-1403">Bespoke Systems Engineering</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" data-id="element-1404">
                Connect Your Tools. <br data-id="element-1405" />
                <span className="text-viridia" data-id="element-1406">Automate the Rest.</span>
              </h1>
              <p className="text-xl text-gray-400 mb-8 max-w-2xl" data-id="element-1407">
                When off-the-shelf software doesn't fit your unique process, we
                build custom workflows that make your existing tools talk to
                each other. Eliminate manual data entry and operational
                bottlenecks.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-1408">
                <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]" data-id="element-1409">
                  Book a Scoping Call
                </Link>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-1410">
              <div className="relative" data-id="element-1411">
                <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 relative overflow-hidden" data-id="element-1412">
                  {/* Workflow Diagram Visualization */}
                  <div className="flex flex-col items-center gap-4 relative z-10 py-4" data-id="element-1413">
                    {/* Trigger */}
                    <motion.div initial={{
                    opacity: 0,
                    y: -20
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    duration: 0.5
                  }} className="bg-viridia-black border border-gray-700 rounded-lg p-3 flex items-center gap-3 w-full max-w-64 shadow-lg" data-id="element-1414">
                      <div className="w-8 h-8 rounded bg-blue-500/20 flex items-center justify-center flex-shrink-0" data-id="element-1415">
                        <FileTextIcon className="w-4 h-4 text-blue-400" data-id="element-1416" />
                      </div>
                      <div className="min-w-0" data-id="element-1417">
                        <div className="text-xs text-gray-500" data-id="element-1418">Trigger</div>
                        <div className="text-sm font-medium text-white truncate" data-id="element-1419">
                          New Quote Form Submitted
                        </div>
                      </div>
                    </motion.div>

                    <div className="w-px h-8 bg-gray-700" data-id="element-1420"></div>

                    {/* Logic Branch */}
                    <motion.div initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    duration: 0.5,
                    delay: 0.3
                  }} className="bg-viridia-black border border-viridia/30 rounded-lg p-3 flex items-center gap-3 w-full max-w-64 shadow-lg relative" data-id="element-1421">
                      <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-px bg-viridia/50" data-id="element-1422"></div>
                      <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-px bg-viridia/50" data-id="element-1423"></div>

                      <div className="w-8 h-8 rounded bg-viridia/20 flex items-center justify-center" data-id="element-1424">
                        <CpuIcon className="w-4 h-4 text-viridia" data-id="element-1425" />
                      </div>
                      <div data-id="element-1426">
                        <div className="text-xs text-gray-500" data-id="element-1427">Action</div>
                        <div className="text-sm font-medium text-white" data-id="element-1428">
                          AI Summarizes Request
                        </div>
                      </div>
                    </motion.div>

                    <div className="flex w-full max-w-md justify-between px-12 relative" data-id="element-1429">
                      <div className="w-px h-8 bg-gray-700 absolute left-1/4 top-0" data-id="element-1430"></div>
                      <div className="w-px h-8 bg-gray-700 absolute right-1/4 top-0" data-id="element-1431"></div>
                      <div className="w-1/2 h-px bg-gray-700 absolute left-1/4 top-0" data-id="element-1432"></div>
                    </div>

                    {/* Outcomes */}
                    <div className="flex gap-4 sm:gap-8 w-full justify-center flex-wrap sm:flex-nowrap" data-id="element-1433">
                      <motion.div initial={{
                      opacity: 0,
                      x: -20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.6
                    }} className="bg-viridia-black border border-gray-700 rounded-lg p-3 flex items-center gap-3 w-full sm:w-48 shadow-lg" data-id="element-1434">
                        <div className="w-8 h-8 rounded bg-green-500/20 flex items-center justify-center" data-id="element-1435">
                          <DatabaseIcon className="w-4 h-4 text-green-400" data-id="element-1436" />
                        </div>
                        <div data-id="element-1437">
                          <div className="text-xs text-gray-500" data-id="element-1438">CRM</div>
                          <div className="text-sm font-medium text-white" data-id="element-1439">
                            Create Record
                          </div>
                        </div>
                      </motion.div>

                      <motion.div initial={{
                      opacity: 0,
                      x: 20
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.8
                    }} className="bg-viridia-black border border-gray-700 rounded-lg p-3 flex items-center gap-3 w-full sm:w-48 shadow-lg" data-id="element-1440">
                        <div className="w-8 h-8 rounded bg-purple-500/20 flex items-center justify-center flex-shrink-0" data-id="element-1441">
                          <MessageSquareIcon className="w-4 h-4 text-purple-400" data-id="element-1442" />
                        </div>
                        <div className="min-w-0" data-id="element-1443">
                          <div className="text-xs text-gray-500" data-id="element-1444">Slack</div>
                          <div className="text-sm font-medium text-white truncate" data-id="element-1445">
                            Alert Sales Team
                          </div>
                        </div>
                      </motion.div>
                    </div>

                    {/* Animated Pulses */}
                    <motion.div animate={{
                    top: ['10%', '90%'],
                    opacity: [0, 1, 0]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'linear'
                  }} className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-viridia rounded-full shadow-[0_0_10px_rgba(16,185,129,0.8)] z-20" data-id="element-1446"></motion.div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Why Custom Automations? */}
      <section className="py-20 bg-viridia-black" data-id="element-1447">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1448">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-1449">
            <ScrollReveal data-id="element-1450">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1451">
                Off-the-Shelf Software Rarely Fits Perfectly
              </h2>
              <p className="text-lg text-gray-400 mb-6 leading-relaxed" data-id="element-1452">
                Most local businesses use 5-10 different software tools to run
                their operations. When these tools don't talk to each other,
                your team ends up doing manual data entry, leads fall through
                the cracks, and scaling becomes impossible.
              </p>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-1453">
                We build the "glue" that connects your systems. Whether it's a
                simple Zapier integration or a complex custom webhook, we
                engineer workflows that match exactly how you do business.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1454">
                <div className="bg-viridia-surface border border-gray-800 rounded-xl p-4 flex items-start gap-3" data-id="element-1455">
                  <AlertCircleIcon className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" data-id="element-1456" />
                  <div data-id="element-1457">
                    <h4 className="text-white font-bold text-sm mb-1" data-id="element-1458">
                      Without Automation
                    </h4>
                    <p className="text-gray-500 text-xs" data-id="element-1459">
                      Manual data entry, delayed follow-ups, human error.
                    </p>
                  </div>
                </div>
                <div className="bg-viridia-surface border border-viridia/30 rounded-xl p-4 flex items-start gap-3" data-id="element-1460">
                  <ZapIcon className="w-5 h-5 text-viridia flex-shrink-0 mt-0.5" data-id="element-1461" />
                  <div data-id="element-1462">
                    <h4 className="text-white font-bold text-sm mb-1" data-id="element-1463">
                      With Viridia
                    </h4>
                    <p className="text-gray-500 text-xs" data-id="element-1464">
                      Instant data sync, automated tasks, scalable operations.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <div className="relative" data-id="element-1465">
              <div className="absolute inset-0 bg-viridia/20 blur-[100px] rounded-full" data-id="element-1466"></div>
              <div className="relative bg-viridia-surface border border-gray-800 rounded-2xl p-8 shadow-2xl" data-id="element-1467">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2" data-id="element-1468">
                  <WrenchIcon className="w-5 h-5 text-viridia" data-id="element-1469" /> Our Approach
                </h3>
                <ul className="space-y-6" data-id="element-1470">
                  <li className="flex items-start gap-4" data-id="element-1471">
                    <div className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center text-viridia font-bold text-sm flex-shrink-0" data-id="element-1472">
                      1
                    </div>
                    <div data-id="element-1473">
                      <h4 className="text-white font-bold mb-1" data-id="element-1474">
                        Process Audit
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1475">
                        We map out your current workflows and identify the
                        bottlenecks costing you time.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-1476">
                    <div className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center text-viridia font-bold text-sm flex-shrink-0" data-id="element-1477">
                      2
                    </div>
                    <div data-id="element-1478">
                      <h4 className="text-white font-bold mb-1" data-id="element-1479">
                        Architecture Design
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1480">
                        We design a robust, scalable system architecture using
                        the right tools (APIs, Webhooks, Zapier, Make).
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-1481">
                    <div className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center text-viridia font-bold text-sm flex-shrink-0" data-id="element-1482">
                      3
                    </div>
                    <div data-id="element-1483">
                      <h4 className="text-white font-bold mb-1" data-id="element-1484">
                        Build & Test
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1485">
                        We build the automation, run extensive edge-case
                        testing, and ensure data integrity.
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4" data-id="element-1486">
                    <div className="w-8 h-8 rounded-full bg-viridia/10 flex items-center justify-center text-viridia font-bold text-sm flex-shrink-0" data-id="element-1487">
                      4
                    </div>
                    <div data-id="element-1488">
                      <h4 className="text-white font-bold mb-1" data-id="element-1489">
                        Deployment & Monitoring
                      </h4>
                      <p className="text-gray-400 text-sm" data-id="element-1490">
                        We launch the system and provide ongoing monitoring to
                        ensure it runs flawlessly 24/7.
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When Custom Makes Sense */}
      <section className="py-20 bg-viridia-surface" data-id="element-1491">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1492">
          <ScrollReveal data-id="element-1493">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1494">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1495">
                When Do You Need Custom Automations?
              </h2>
              <p className="text-gray-400" data-id="element-1496">
                Our standard packages cover 90% of local business needs. Custom
                automations are for the 10% that makes your business unique.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1497">
            <ScrollReveal data-id="element-1498">
              <div className="space-y-6" data-id="element-1499">
                <div className="flex items-start gap-4" data-id="element-1500">
                  <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1501">
                    <LayersIcon className="w-5 h-5 text-viridia" data-id="element-1502" />
                  </div>
                  <div data-id="element-1503">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-1504">
                      Disconnected Software Stack
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-1505">
                      Your field service software (like ServiceTitan or Jobber)
                      doesn't talk to your CRM or accounting software, forcing
                      you to enter data twice.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-1506">
                  <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1507">
                    <AlertCircleIcon className="w-5 h-5 text-viridia" data-id="element-1508" />
                  </div>
                  <div data-id="element-1509">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-1510">
                      Complex Approval Processes
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-1511">
                      Quotes over a certain amount need manager approval, or
                      specific job types need to be routed to specific
                      technicians based on logic.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-1512">
                  <div className="w-10 h-10 rounded-lg bg-viridia/10 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1513">
                    <DatabaseIcon className="w-5 h-5 text-viridia" data-id="element-1514" />
                  </div>
                  <div data-id="element-1515">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-1516">
                      Heavy Admin Burden
                    </h3>
                    <p className="text-gray-400 text-sm" data-id="element-1517">
                      Your team spends hours every week copy-pasting information
                      from emails into spreadsheets or generating manual
                      reports.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-1518">
              <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8" data-id="element-1519">
                <h3 className="text-xl font-bold text-white mb-6 text-center" data-id="element-1520">
                  The Cost of Manual Work
                </h3>
                <div className="space-y-6" data-id="element-1521">
                  <div data-id="element-1522">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1523">
                      <span className="text-gray-400" data-id="element-1524">Manual Data Entry</span>
                      <span className="text-red-400" data-id="element-1525">High Error Rate</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2" data-id="element-1526">
                      <div className="bg-red-500 h-2 rounded-full" style={{
                      width: '85%'
                    }} data-id="element-1527"></div>
                    </div>
                  </div>
                  <div data-id="element-1528">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1529">
                      <span className="text-gray-400" data-id="element-1530">Information Silos</span>
                      <span className="text-yellow-400" data-id="element-1531">Slow Handoffs</span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2" data-id="element-1532">
                      <div className="bg-yellow-500 h-2 rounded-full" style={{
                      width: '70%'
                    }} data-id="element-1533"></div>
                    </div>
                  </div>
                  <div className="pt-6 border-t border-gray-800" data-id="element-1534">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1535">
                      <span className="text-white font-medium" data-id="element-1536">
                        Automated System
                      </span>
                      <span className="text-viridia font-medium" data-id="element-1537">
                        Instant & Accurate
                      </span>
                    </div>
                    <div className="w-full bg-gray-800 rounded-full h-2" data-id="element-1538">
                      <div className="bg-viridia h-2 rounded-full" style={{
                      width: '100%'
                    }} data-id="element-1539"></div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Examples Section */}
      <section className="py-20 bg-viridia-surface" data-id="element-1540">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1541">
          <ScrollReveal data-id="element-1542">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1543">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1544">
                Examples of Custom Workflows
              </h2>
              <p className="text-gray-400" data-id="element-1545">
                If it has an API, we can connect it. Here are a few examples of
                systems we can build.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1546">
            {[{
            title: 'Intelligent Lead Routing',
            desc: 'Web form submissions are analyzed by AI. High-value commercial leads are texted directly to the owner, while residential leads go to the standard sales queue.',
            icon: GitMergeIcon
          }, {
            title: 'Cross-Platform Syncing',
            desc: 'When a job is marked "Complete" in Jobber, automatically generate an invoice in QuickBooks and send a review request via Viridia CRM.',
            icon: WorkflowIcon
          }, {
            title: 'AI Call Summaries to Slack',
            desc: "After every recorded sales call, AI generates a 3-bullet summary of the customer's needs and posts it to a Slack channel for the fulfillment team.",
            icon: MessageSquareIcon
          }, {
            title: 'Automated Reporting',
            desc: 'Pull data from Google Ads, Facebook Ads, and your CRM into a custom Google Sheet that updates daily without manual export/import.',
            icon: FileTextIcon
          }, {
            title: 'Inventory Alerts',
            desc: 'When a specific service is booked, automatically check inventory levels in your management software and alert the purchasing manager if parts are needed.',
            icon: AlertCircleIcon
          }, {
            title: 'Custom Webhooks',
            desc: 'Need your proprietary internal software to trigger actions in modern marketing tools? We build the middleware to make it happen.',
            icon: SettingsIcon
          }].map((example, index) => <ScrollReveal key={index} delay={index * 0.1} data-id="element-1547">
                <div className="bg-viridia-black border border-gray-800 rounded-xl p-6 h-full hover:border-viridia/30 transition-colors" data-id="element-1548">
                  <div className="w-10 h-10 rounded bg-viridia-surface border border-gray-700 flex items-center justify-center mb-4" data-id="element-1549">
                    <example.icon className="w-5 h-5 text-viridia" data-id="element-1550" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-1551">
                    {example.title}
                  </h3>
                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-1552">
                    {example.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-viridia-black" data-id="element-1553">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1554">
          <ScrollReveal data-id="element-1555">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1556">
              <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1557">
                Our Engineering Process
              </h2>
              <p className="text-gray-400" data-id="element-1558">
                Custom builds require careful planning. We don't write a line of
                code until we understand your business logic.
              </p>
            </div>
          </ScrollReveal>

          <div className="relative" data-id="element-1559">
            {/* Connecting line */}
            <div className="hidden lg:block absolute top-12 left-0 w-full h-0.5 bg-gray-800 z-0" data-id="element-1560"></div>

            <div className="grid lg:grid-cols-5 gap-8" data-id="element-1561">
              {[{
              step: '1',
              title: 'Discover',
              desc: 'We map out your current manual process and identify bottlenecks.',
              icon: SearchIcon
            }, {
              step: '2',
              title: 'Architect',
              desc: 'We design the logic flow and select the right APIs and tools.',
              icon: MapIcon
            }, {
              step: '3',
              title: 'Build',
              desc: 'We engineer the connections, webhooks, and data transformations.',
              icon: WrenchIcon
            }, {
              step: '4',
              title: 'Test',
              desc: 'Rigorous testing with edge cases to ensure data integrity.',
              icon: ActivityIcon
            }, {
              step: '5',
              title: 'Launch',
              desc: 'Deployment, team training, and ongoing monitoring.',
              icon: RocketIcon
            }].map((phase, index) => <ScrollReveal key={index} delay={index * 0.1} data-id="element-1562">
                  <div className="relative z-10 flex flex-col items-center text-center" data-id="element-1563">
                    <div className="w-24 h-24 rounded-full bg-viridia-surface border-4 border-viridia-black flex items-center justify-center mb-4 shadow-[0_0_0_2px_rgba(31,41,55,1)]" data-id="element-1564">
                      <phase.icon className="w-8 h-8 text-viridia" data-id="element-1565" />
                    </div>
                    <div className="text-xs font-bold text-viridia mb-1" data-id="element-1566">
                      PHASE {phase.step}
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-1567">
                      {phase.title}
                    </h3>
                    <p className="text-sm text-gray-400" data-id="element-1568">{phase.desc}</p>
                  </div>
                </ScrollReveal>)}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-1569">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-1570">
          <ScrollReveal data-id="element-1571">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1572">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-400" data-id="element-1573">
              Everything you need to know about our custom automation services.
            </p>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1574">
          <ScrollReveal delay={0.2} data-id="element-1575">
            <div className="bg-viridia-black border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-1576">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-1577" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Scope Clarity Section */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-1578">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1579">
          <div className="bg-viridia-black border border-gray-800 rounded-3xl overflow-hidden" data-id="element-1580">
            <div className="grid md:grid-cols-2" data-id="element-1581">
              <div className="p-10 border-b md:border-b-0 md:border-r border-gray-800" data-id="element-1582">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-1583">
                  Standard Packages
                </h3>
                <p className="text-gray-400 text-sm mb-6" data-id="element-1584">
                  Our standard AI Lead Conversion and CRM setups are pre-built
                  systems designed for rapid deployment.
                </p>
                <ul className="space-y-3" data-id="element-1585">
                  <li className="flex items-center gap-2 text-sm text-gray-300" data-id="element-1586">
                    <CheckCircleIcon className="w-4 h-4 text-gray-500" data-id="element-1587" /> Fixed
                    monthly pricing
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300" data-id="element-1588">
                    <CheckCircleIcon className="w-4 h-4 text-gray-500" data-id="element-1589" />{' '}
                    Deployed in 7 days
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300" data-id="element-1590">
                    <CheckCircleIcon className="w-4 h-4 text-gray-500" data-id="element-1591" />{' '}
                    Proven, templated workflows
                  </li>
                </ul>
              </div>
              <div className="p-10 bg-gradient-to-br from-viridia/5 to-transparent" data-id="element-1592">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-1593">
                  Custom Automations
                </h3>
                <p className="text-gray-400 text-sm mb-6" data-id="element-1594">
                  Bespoke engineering work to connect third-party tools or build
                  entirely unique logic for your business.
                </p>
                <ul className="space-y-3" data-id="element-1595">
                  <li className="flex items-center gap-2 text-sm text-gray-300" data-id="element-1596">
                    <CheckCircleIcon className="w-4 h-4 text-viridia" data-id="element-1597" /> Scoped
                    and quoted per project
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300" data-id="element-1598">
                    <CheckCircleIcon className="w-4 h-4 text-viridia" data-id="element-1599" />{' '}
                    Timeline based on complexity
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300" data-id="element-1600">
                    <CheckCircleIcon className="w-4 h-4 text-viridia" data-id="element-1601" /> 100%
                    tailored to your operations
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-viridia-black border-t border-gray-800" data-id="element-1602">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1603">
          <ScrollReveal data-id="element-1604">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1605">
              Let's Map Out Your System
            </h2>
            <p className="text-xl text-gray-400 mb-10" data-id="element-1606">
              Book a technical scoping call. We'll review your current software
              stack and identify exactly where automation can save you time and
              money.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium text-white bg-viridia rounded-lg hover:bg-viridia-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)]" data-id="element-1607">
              Book a Scoping Call
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}